// Сервис для чтения Purchase Orders из IndexedDB.
// Используется в SignTimesheetModal — foreman при подписи недели может
// отметить какие POs были использованы по каждому job (Material / Equipment /
// Subcontractor) и оставить общий голосовой комментарий.
//
// Категория = costCode startsWith определённого префикса. Сама единая таблица
// POs синхронится через syncConfig (ключ purchaseOrders, /equipment/purchase-orders).

import { initializeDB } from '../config/db';
import { PURCHASE_ORDER_STORE } from '../config/constants';

/**
 * Категории покупок: prefix costCode → seamless метка для UI.
 * Прочие префиксы попадают в "other" и не показываются в табах подписи.
 */
export const PO_CATEGORIES = [
    { key: 'material',     label: 'Material',     prefix: '06-100', color: '#0891b2' },
    { key: 'equipment',    label: 'Equipment',    prefix: '04-500', color: '#ea580c' },
    { key: 'subcontractor', label: 'Subcontractor', prefix: '07-100', color: '#7c3aed' },
];

const PREFIX_BY_KEY = Object.fromEntries(PO_CATEGORIES.map(c => [c.key, c.prefix]));

export function getCategoryByCostCode(costCode) {
    if (!costCode) return null;
    const trimmed = String(costCode).trim();
    for (const cat of PO_CATEGORIES) {
        if (trimmed.startsWith(cat.prefix)) return cat.key;
    }
    return null;
}

/**
 * Все POs из IDB для указанного списка job-кодов (или всех, если null).
 * Фильтрация по списку категорий PO_CATEGORIES — прочие costCode'ы отбрасываются.
 * Жирный фильтр выполняется один раз, дальнейшая категоризация делается в UI.
 *
 * @param {string[]|null} jobCodes — список job-кодов (например ['8199', '7560']) или null чтобы взять все.
 * @returns {Promise<object[]>}  POs со всеми оригинальными полями + _category, _costCode, _searchBlob (lowercase).
 */
export async function getPurchaseOrdersForJobs(jobCodes) {
    console.log('[PO] step 1: opening IDB…');
    const db = await initializeDB();
    console.log('[PO] step 2: IDB opened, version:', db.version, 'stores:',
        Array.from(db.objectStoreNames));
    if (!db.objectStoreNames.contains(PURCHASE_ORDER_STORE)) {
        console.warn('[PO] store', PURCHASE_ORDER_STORE, 'NOT FOUND in IDB — needs sync');
        return [];
    }
    console.log('[PO] step 3: opening transaction on', PURCHASE_ORDER_STORE);
    const tx = db.transaction(PURCHASE_ORDER_STORE, 'readonly');
    console.log('[PO] step 4: getAll…');
    const all = await tx.store.getAll();
    console.log('[PO] step 5: getAll returned', all.length, 'rows, awaiting tx.done…');
    await tx.done;
    console.log('[PO] step 6: tx done');

    const jobSet = jobCodes && jobCodes.length > 0
        ? new Set(jobCodes.map(j => String(j).trim()))
        : null;

    // Diagnostics — печатает в консоли где обрезаем строки. Удалить когда
    // подтвердим что фильтрация работает на реальных данных.
    let droppedStatus = 0;
    let droppedCategory = 0;
    let droppedJob = 0;
    const sample = all.slice(0, 3).map(p => ({
        po: p.po, job: p.job, costCode: p.costCode, status: p.status,
    }));
    console.log('[PO] total in IDB:', all.length, 'looking for jobs:', jobCodes, 'sample:', sample);

    const results = [];
    for (const po of all) {
        if (po.status === false) { droppedStatus++; continue; }
        const costCode = (po.costCode || '').trim();
        const category = getCategoryByCostCode(costCode);
        if (!category) { droppedCategory++; continue; }
        const jobNum = String(po.job || '').trim();
        if (jobSet && !jobSet.has(jobNum)) { droppedJob++; continue; }

        const poNum = String(po.po || '');
        const vendor = String(po.vendor || '');
        const description = String(po.description || '');
        const equipment = String(po.equipment || '');
        const location = String(po.location || '');

        results.push({
            ...po,
            _category: category,
            _costCode: costCode,
            _po: poNum,
            _vendor: vendor,
            _description: description,
            _equipment: equipment,
            _location: location,
            _job: jobNum,
            // Один lowercase-blob для быстрого поиска по всем полям сразу.
            _searchBlob: `${poNum} ${vendor} ${description} ${equipment} ${location} ${costCode} ${jobNum}`.toLowerCase(),
        });
    }
    console.log('[PO] kept:', results.length,
        '· dropped — status:', droppedStatus,
        '· wrong-cost-code:', droppedCategory,
        '· wrong-job:', droppedJob);
    return results;
}

/**
 * Нормализация поискового запроса — удаляет дефисы/точки и опускает регистр.
 * Так пользователь может искать "06100" и попадать на costCode "06-100",
 * либо вводить часть PO без беспокойства про форматирование.
 */
export function normalizePoSearch(str) {
    if (!str) return '';
    return String(str).replace(/[-.]/g, '').toLowerCase();
}

export default {
    PO_CATEGORIES,
    getCategoryByCostCode,
    getPurchaseOrdersForJobs,
    normalizePoSearch,
};
